/**
 * API Helper для управления сервисами судов
 * Требует авторизацию через JWT токен
 * 
 * Все запросы идут через centralized elBackAxios
 */

import { elBackAxios } from '@/services/myAxios'

/**
 * Загрузить все корабли и их сервисы (для Admin Panel)
 * @returns {Promise<Array>}
 */
export async function getAllDevicesWithServices() {
  try {
    const response = await elBackAxios.get('/api/services/admin/all-devices')
    return response.data
  } catch (error) {
    console.error('Failed to load devices:', error)
    throw error
  }
}

/**
 * Получить сервисы конкретного корабля
 * @param {string} deviceId
 * @returns {Promise<Array>}
 */
export async function getDeviceServices(deviceId) {
  try {
    const response = await elBackAxios.get(`/api/services/${deviceId}`)
    return response.data
  } catch (error) {
    console.error(`Failed to load services for device ${deviceId}:`, error)
    throw error
  }
}

/**
 * Подписать корабль на сервис
 * @param {string} deviceId
 * @param {string} serviceType - AIS, Navarea, Weather, Catalog
 * @param {string|null} endDate - ISO date string (null for unlimited)
 * @param {string|null} notes
 * @returns {Promise<Object>}
 */
export async function subscribeToService(deviceId, serviceType, endDate = null, notes = null) {
  try {
    const payload = {
      service_type: serviceType
    }

    if (endDate) {
      payload.end_date = endDate
    }

    if (notes) {
      payload.notes = notes
    }

    const response = await elBackAxios.post(`/api/services/${deviceId}/subscribe`, payload)
    return response.data
  } catch (error) {
    console.error(`Failed to subscribe to ${serviceType}:`, error)
    throw error
  }
}

/**
 * Приостановить сервис
 * @param {string} deviceId
 * @param {string} serviceType
 * @returns {Promise<Object>}
 */
export async function pauseService(deviceId, serviceType) {
  try {
    const response = await elBackAxios.post(`/api/services/${deviceId}/pause/${serviceType}`)
    return response.data
  } catch (error) {
    console.error(`Failed to pause ${serviceType}:`, error)
    throw error
  }
}

/**
 * Возобновить сервис
 * @param {string} deviceId
 * @param {string} serviceType
 * @returns {Promise<Object>}
 */
export async function resumeService(deviceId, serviceType) {
  try {
    const response = await elBackAxios.post(`/api/services/${deviceId}/resume/${serviceType}`)
    return response.data
  } catch (error) {
    console.error(`Failed to resume ${serviceType}:`, error)
    throw error
  }
}

/**
 * Отписать корабль от сервиса
 * @param {string} deviceId
 * @param {string} serviceType
 * @returns {Promise<Object>}
 */
export async function unsubscribeFromService(deviceId, serviceType) {
  try {
    const response = await elBackAxios.post(`/api/services/${deviceId}/unsubscribe/${serviceType}`)
    return response.data
  } catch (error) {
    console.error(`Failed to unsubscribe from ${serviceType}:`, error)
    throw error
  }
}

/**
 * Получить статус сервиса с цветовым кодом
 * @param {string} status
 * @returns {Object}
 */
export function getStatusInfo(status) {
  const statusMap = {
    'Active': {
      color: 'success',
      icon: '🟢',
      label: 'Active',
      badge: 'success'
    },
    'Paused': {
      color: 'danger',
      icon: '🔴',
      label: 'Paused',
      badge: 'danger'
    },
    'Canceled': {
      color: 'secondary',
      icon: '⚪',
      label: 'Canceled',
      badge: 'secondary'
    },
    'Expired': {
      color: 'warning',
      icon: '🟡',
      label: 'Expired',
      badge: 'warning'
    }
  }

  return statusMap[status] || statusMap['Canceled']
}

/**
 * Проверить, является ли дата истекшей
 * @param {string|null} endDate
 * @returns {boolean}
 */
export function isDateExpired(endDate) {
  if (!endDate) {
    return false
  }

  const end = new Date(endDate)
  const now = new Date()
  return end < now
}

/**
 * Форматировать дату для отображения
 * @param {string|null} date
 * @returns {string}
 */
export function formatDate(date) {
  if (!date) {
    return 'Unlimited'
  }

  try {
    const d = new Date(date)
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  } catch {
    return date
  }
}

/**
 * Получить статус всех сервисов (health check)
 * @returns {Promise<Object>}
 */
export async function getAllServicesHealth() {
  try {
    const response = await elBackAxios.get('/api/services/admin/health')
    return response.data
  } catch (error) {
    console.error('Failed to get services health:', error)
    throw error
  }
}

/**
 * Получить статус конкретного сервиса (health check)
 * @param {string} serviceType - Тип сервиса (AIS, Navarea, Weather, Catalog)
 * @returns {Promise<Object>}
 */
export async function getServiceHealth(serviceType) {
  try {
    const response = await elBackAxios.get(`/api/services/admin/health/${serviceType}`)
    return response.data
  } catch (error) {
    console.error(`Failed to get ${serviceType} health:`, error)
    throw error
  }
}

export default {
  getAllDevicesWithServices,
  getDeviceServices,
  subscribeToService,
  pauseService,
  resumeService,
  unsubscribeFromService,
  getStatusInfo,
  isDateExpired,
  formatDate,
  getAllServicesHealth,
  getServiceHealth
}
